import type { Keyboard } from "@/domain/types";
import { type ImportedKeyboard, importKeyboardPair, renameBoardLayer } from "./keyboard-import";
import { type CodeOverride, importKLE, type KLEData, lenientKLE } from "./kle-import";
import sixtyKle from "./sources/60-ansi.kle.json";
import sixtyIsoKle from "./sources/60-iso.kle.json";
import eloraKeymap from "./sources/elora.qmk.json";
import eloraInfo from "./sources/elora.qmk-info.json";
import ergodoxKeymap from "./sources/ergodox.qmk.json";
import ergodoxInfo from "./sources/ergodox.qmk-info.json";
import fullKle from "./sources/full-ansi104.kle.json";
import isoFullKle from "./sources/full-iso105.kle.json";
import kinesisRaw from "./sources/kinesis-advantage.kle.txt" with { type: "text" };
import kyriaKeymap from "./sources/kyria.qmk.json";
import kyriaInfo from "./sources/kyria.qmk-info.json";
import moonlanderKeymap from "./sources/moonlander.qmk.json";
import moonlanderInfo from "./sources/moonlander.qmk-info.json";
import recoreDef from "./sources/recorev3.via-definition.json";
import recoreKeymap from "./sources/recorev3.via-keymap.json";
import redoxDef from "./sources/redox.via-definition.json";
import redoxKeymap from "./sources/redox.via-keymap.json";
import tklAnsiKle from "./sources/tkl-ansi.kle.json";
import tklKle from "./sources/tkl-iso.kle.json";

// board-declared fixed names for specific board layers: a built-in board may
// name its layers by id, since QMK/VIA store none. Undeclared layers keep the auto-named "Layer N",
// and built-in names are not renamable (only session imports rename). Display only - resolution is
// by layer id, so a name never moves a Binding.
function namedLayers(imported: ImportedKeyboard, names: Record<string, string>): ImportedKeyboard {
  return Object.entries(names).reduce(
    (acc, [layerId, name]) => renameBoardLayer(acc, layerId, name),
    imported,
  );
}

// the importer is the single source of geometry: recore parses through the
// same VIA path an upload takes, so its board layers ship intact. The maintainer's
// gaming layers carry their real names: L4 the FPS remap, L5 the StarCraft II remap.
const recore = namedLayers(
  importKeyboardPair(
    { format: "via-definition", data: recoreDef, name: null },
    { format: "via-keymap", data: recoreKeymap, name: null },
    { id: "recore-v3" },
  ),
  { "recore-v3-layer-4": "FPS", "recore-v3-layer-5": "SC2" },
);

// a clean user-facing display name over the file-derived one (e.g. "splitkb/halcyon/elora/rev2",
// "Redox-W"); a label only, no geometry touched. See sources/PROVENANCE.md.
function named(imported: ImportedKeyboard, name: string): ImportedKeyboard {
  return { ...imported, keyboard: { ...imported.keyboard, name } };
}

// five split / ergo boards from real vendored exports; the VIA/QMK pairs carry their
// board layers. Provenance + licences: sources/PROVENANCE.md.
const moonlander = named(
  importKeyboardPair(
    { format: "qmk-info", data: moonlanderInfo, name: null },
    { format: "qmk-keymap", data: moonlanderKeymap, name: null },
    { id: "moonlander" },
  ),
  "ZSA Moonlander",
);
const redox = named(
  importKeyboardPair(
    { format: "via-definition", data: redoxDef, name: null },
    { format: "via-keymap", data: redoxKeymap, name: null },
    { id: "redox" },
  ),
  "Redox",
);
const kyria = named(
  importKeyboardPair(
    { format: "qmk-info", data: kyriaInfo, name: null },
    { format: "qmk-keymap", data: kyriaKeymap, name: null },
    { id: "kyria" },
  ),
  "splitkb Kyria",
);
const elora = named(
  importKeyboardPair(
    { format: "qmk-info", data: eloraInfo, name: null },
    { format: "qmk-keymap", data: eloraKeymap, name: null },
    { id: "elora" },
  ),
  "splitkb Elora",
);
// ErgoDox: the vendored QMK pair (real keycodes on all 76 keys + 3 board layers) replaces the
// blank-legend community KLE, whose 36 placeholder caps could bind nothing. Deliberate trades
// (maintainer decision, 2026-07-02): the QMK info.json carries no rotation, so the thumb
// clusters render flat, and a multi-layer board offers no universal Keyboard Layouts.
const ergodox = named(
  importKeyboardPair(
    { format: "qmk-info", data: ergodoxInfo, name: null },
    { format: "qmk-keymap", data: ergodoxKeymap, name: null },
    { id: "ergodox" },
  ),
  "ErgoDox",
);

// The curated code residue: the few keys whose legend genuinely carries no code signal (see
// applyCodeOverrides + decisions.md). The ISO 102nd key's "|\" legend is byte-identical to the
// ANSI Backslash key's, and the Kinesis up-arrow uses KLE's standard Shift glyph - no importer
// inference can tell them apart; everything else on these boards is inferred, never hand-coded.
const ISO_102ND = (index: number): CodeOverride[] => [
  { index, legend: "|\n\\", code: "IntlBackslash" },
];

// Kinesis Advantage: a community flat 2D approximation in KLE raw-data form (lenientKLE parses it).
// Single board layer, like the other KLE boards. PROVENANCE.md documents the approximation.
const kinesis = importKLE(
  lenientKLE(kinesisRaw),
  {
    id: "kinesis-advantage",
    name: "Kinesis Advantage",
  },
  [{ index: 69, legend: "⇧\n\n\n\n⇧", code: "ArrowUp" }],
);

/** VIA/QMK-sourced built-ins keep their board layers; parsed once at load. */
export const builtinEntries: Record<string, ImportedKeyboard> = {
  "recore-v3": recore,
  moonlander,
  redox,
  kyria,
  elora,
  ergodox,
};

// the vendored KLE JSON imports are typed as their inferred literal shape, not KLEData; this one
// cast site funnels them through the importer (the single source of geometry).
const kle = (
  raw: unknown,
  meta: { id: string; name: string },
  overrides?: CodeOverride[],
): Keyboard => importKLE(raw as KLEData, meta, overrides).keyboard;

/** Built-in boards, parsed from vendored real exports, KLE or VIA (no fabricated coordinates). */
export const builtins: Record<string, Keyboard> = {
  "ansi-104": kle(fullKle, { id: "ansi-104", name: "Full-size (ANSI)" }),
  "iso-105": kle(isoFullKle, { id: "iso-105", name: "Full-size (ISO)" }, ISO_102ND(75)),
  "ansi-tkl": kle(tklAnsiKle, { id: "ansi-tkl", name: "Tenkeyless (ANSI)" }),
  "iso-tkl": kle(tklKle, { id: "iso-tkl", name: "Tenkeyless (ISO)" }, ISO_102ND(64)),
  "ansi-60": kle(sixtyKle, { id: "ansi-60", name: "60% (ANSI)" }),
  "iso-60": kle(sixtyIsoKle, { id: "iso-60", name: "60% (ISO)" }, ISO_102ND(42)),
  ergodox: ergodox.keyboard,
  "recore-v3": recore.keyboard,
  moonlander: moonlander.keyboard,
  redox: redox.keyboard,
  kyria: kyria.keyboard,
  elora: elora.keyboard,
  "kinesis-advantage": kinesis.keyboard,
};

export const builtinOrder = [
  "ansi-104",
  "iso-105",
  "ansi-tkl",
  "iso-tkl",
  "ansi-60",
  "iso-60",
  "ergodox",
  "recore-v3",
  "moonlander",
  "redox",
  "kyria",
  "elora",
  "kinesis-advantage",
] as const;
